import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { payrollPaymentService } from '@/services/payrollPaymentService'
import { employeesService } from '@/services/employeesService'
import type { PayrollPaymentLineItem, PayrollPaymentLinesResponse } from '@/api/types/payroll.types'
import { showSuccess } from '@/lib/swal'

export const PayrollPaymentView: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const location = useLocation()
  const { user } = useAuthStore()
  const branchId = Number(user?.selectedBranch?.branchId ?? 0)

  const paymentId = Number(params.id ?? 0)

  const [rows, setRows] = useState<PayrollPaymentLineItem[]>([])
  const [loading, setLoading] = useState(false)
  const [updatingLineId, setUpdatingLineId] = useState<number | null>(null)
  const [masterPaymentDateIso, setMasterPaymentDateIso] = useState<string>('')
  const [masterIsPosted, setMasterIsPosted] = useState<boolean>(Boolean((location.state as any)?.isPosted))
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [employeeNameById, setEmployeeNameById] = useState<Record<number, string>>({})
  const [editsByLineId, setEditsByLineId] = useState<Record<number, { amount: string; remainingAmount: string }>>({})

  useEffect(() => {
    if (!branchId || !paymentId) return
    let cancelled = false
    setLoading(true)
    payrollPaymentService
      .getLinesByPaymentIdWithPaging(paymentId, branchId, pageNumber, pageSize)
      .then(async (res: PayrollPaymentLinesResponse) => {
        if (cancelled) return
        const list = res.data ?? []
        setRows(list)
        setTotalRecords(res.totalRecords ?? 0)

        // Initialize editable values
        const nextEdits: Record<number, { amount: string; remainingAmount: string }> = {}
        for (const r of list) {
          const id = Number(r.id ?? 0)
          if (!id) continue
          nextEdits[id] = {
            amount: String(r.amount ?? 0),
            remainingAmount: String(r.remainingAmount ?? 0),
          }
        }
        setEditsByLineId(nextEdits)

        // Build employee name map
        try {
          const empRes = await employeesService.getAllEmployees(branchId, 1, 10000)
          const map: Record<number, string> = {}
          for (const e of empRes.data ?? []) {
            const id = Number((e as any)?.id ?? 0)
            if (!id) continue
            const name = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()
            if (name) map[id] = name
          }
          setEmployeeNameById(map)
        } catch {
          setEmployeeNameById({})
        }

        // Load master payment date (needed for Update/{id}/{branchId})
        try {
          const master = await payrollPaymentService.getById(paymentId)
          const dt = String((master as any)?.paymentDate ?? (master as any)?.date ?? '').trim()
          setMasterPaymentDateIso(dt || '')
          setMasterIsPosted(Boolean((master as any)?.isPosted))
        } catch {
          setMasterPaymentDateIso('')
        }
      })
      .catch((err: any) => {
        if (cancelled) return
        setRows([])
        setTotalRecords(0)
        toast.error(err?.response?.data?.message || err?.message || 'Failed to load payroll payment lines')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [branchId, paymentId, pageNumber, pageSize])

  const updateEdit = (lineId: number, patch: Partial<{ amount: string; remainingAmount: string }>) => {
    setEditsByLineId((prev) => ({
      ...prev,
      [lineId]: { ...(prev[lineId] ?? { amount: '0', remainingAmount: '0' }), ...patch },
    }))
  }

  const handleUpdateLine = async (row: PayrollPaymentLineItem) => {
    const lineId = Number(row.id ?? 0)
    if (!branchId || !paymentId || !lineId) return
    if (masterIsPosted) return
    if (updatingLineId != null) return

    const edit = editsByLineId[lineId]
    const amount = Number(edit?.amount ?? row.amount ?? 0)
    const remainingAmount = Number(edit?.remainingAmount ?? row.remainingAmount ?? 0)
    const employeeId = Number(row.empId ?? 0)
    const paymentDate = masterPaymentDateIso || new Date().toISOString()

    if (!employeeId) {
      toast.error('Invalid employee')
      return
    }
    if (!Number.isFinite(amount) || amount < 0) {
      toast.error('Invalid amount')
      return
    }
    if (!Number.isFinite(remainingAmount) || remainingAmount < 0) {
      toast.error('Invalid remaining amount')
      return
    }

    setUpdatingLineId(lineId)
    try {
      await payrollPaymentService.updateByIdBranch(paymentId, branchId, {
        paymentDate,
        payrollPaymentDetails: [
          {
            id: lineId,
            employeeId,
            amount,
            remainingAmount,
          },
        ],
      })
      await showSuccess(
        t('payrollPaymentPage.lineUpdatedTitle', { defaultValue: 'Updated' }),
        t('payrollPaymentPage.lineUpdatedMessage', { defaultValue: 'Payment line updated successfully.' }),
        2000,
      )
      setRows((prev) =>
        prev.map((r) =>
          Number(r.id ?? 0) === lineId ? { ...r, amount, remainingAmount } : r,
        ),
      )
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Update failed')
    } finally {
      setUpdatingLineId(null)
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize))
  const pagFrom = totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const pagTo = totalRecords === 0 ? 0 : Math.min(pageNumber * pageSize, totalRecords)

  const rowsDisplay = useMemo(() => {
    return rows.map((r) => {
      const eid = Number(r.empId ?? 0)
      return {
        ...r,
        employeeName: (eid && employeeNameById[eid]) ? employeeNameById[eid] : (eid ? `Employee ${eid}` : '—'),
      }
    })
  }, [rows, employeeNameById])

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('payrollPaymentPage.pageTitle')} description={t('payrollPaymentPage.viewTitle')} />
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t('payrollPaymentPage.selectBranchList')}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title={t('payrollPaymentPage.pageTitle')} description={t('payrollPaymentPage.viewTitle')}>
        <Button variant="outline" onClick={() => navigate('/payroll/payroll-payment')}>
          {t('payrollPaymentPage.back', { defaultValue: 'Back' })}
        </Button>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>{t('payrollPaymentPage.paymentLinesLabel', { defaultValue: 'Payment lines' })}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border overflow-x-auto">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-center">{t('payrollPaymentPage.colEmployeeName')}</TableHead>
                    <TableHead className="text-center">{t('payrollPaymentPage.colPaymentAmount')}</TableHead>
                    <TableHead className="text-center">{t('payrollPaymentPage.colRemainingAmount')}</TableHead>
                    <TableHead className="text-center">{t('payrollPaymentPage.colUpdate', { defaultValue: 'Update' })}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rowsDisplay.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        {t('payrollPaymentPage.empty', { defaultValue: 'No payroll payments found' })}
                      </TableCell>
                    </TableRow>
                  ) : (
                    rowsDisplay.map((row) => (
                      <TableRow key={row.id ?? `${row.empId}-${row.amount}`}>
                        <TableCell className="text-center">{(row as any).employeeName}</TableCell>
                        <TableCell className="text-center">
                          <Input
                            value={editsByLineId[Number(row.id ?? 0)]?.amount ?? String(row.amount ?? 0)}
                            onChange={(e) => updateEdit(Number(row.id ?? 0), { amount: e.target.value })}
                            disabled={updatingLineId != null || masterIsPosted}
                            readOnly={masterIsPosted}
                            className="h-9 w-32 mx-auto text-center"
                            inputMode="decimal"
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Input
                            value={editsByLineId[Number(row.id ?? 0)]?.remainingAmount ?? String(row.remainingAmount ?? 0)}
                            onChange={(e) => updateEdit(Number(row.id ?? 0), { remainingAmount: e.target.value })}
                            disabled={updatingLineId != null || masterIsPosted}
                            readOnly={masterIsPosted}
                            className="h-9 w-32 mx-auto text-center"
                            inputMode="decimal"
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void handleUpdateLine(row)}
                            disabled={!row.id || updatingLineId != null || masterIsPosted}
                          >
                            {updatingLineId === Number(row.id) ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              t('payrollPaymentPage.colUpdate', { defaultValue: 'Update' })
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
            <span>
              {t('payrollPaymentPage.paginationShowing', { from: pagFrom, to: pagTo, total: totalRecords })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber <= 1}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
                {t('payrollPaymentPage.previous')}
              </Button>
              <span>{t('payrollPaymentPage.pageOf', { page: pageNumber, totalPages })}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber >= totalPages}
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
              >
                {t('payrollPaymentPage.next')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

